import React, { useMemo, useState } from 'react';
import {
    Card, Input, Radio, Select, Button, Space, List,
    InputNumber as AntInputNumber, Typography, Modal
} from 'antd';
import { ArrowLeftOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import InputNumber from 'components/input-number';
import { formatCurrency } from 'utils/number';
import TagPicker from 'components/pickers/product_tag';
import ProductPicker from 'components/pickers/product';
import { toSearchText } from 'utils/text';

const { Text } = Typography;

const ModeOptions = [
    { label: 'Cố định', value: 'manual' },
    { label: 'Tự động', value: 'auto' },
];

const ApplyToOptions = [
    { label: 'Tất cả SP', value: 'all' },
    { label: 'Theo tag', value: 'tag' },
    { label: 'Theo danh mục', value: 'category' },
    { label: 'Thủ công', value: 'manual' },
];

const AutoRuleTypeOptions = [
    { label: '% giảm', value: 'percent' },
    { label: 'Số tiền', value: 'fixed_amount' },
];

const MobilePriceEditModal = ({ row, priceItems, onPriceItemChange, siblingVariants }) => {
    const [open, setOpen] = useState(false);
    const [editPrice, setEditPrice] = useState(0);
    const [editTiers, setEditTiers] = useState([]);

    const openModal = () => {
        setEditPrice(priceItems[row.id]?.original_price ?? row.original_price ?? 0);
        setEditTiers([...(priceItems[row.id]?.price_list ?? row.price_list ?? [])]);
        setOpen(true);
    };

    const handleApply = () => {
        onPriceItemChange(row.id, 'original_price', editPrice);
        onPriceItemChange(row.id, 'price_list', editTiers);
        setOpen(false);
    };

    const handleApplyToProduct = () => {
        (siblingVariants || []).forEach(v => {
            onPriceItemChange(v.id, 'original_price', editPrice);
            onPriceItemChange(v.id, 'price_list', editTiers);
        });
        onPriceItemChange(row.id, 'original_price', editPrice);
        onPriceItemChange(row.id, 'price_list', editTiers);
        setOpen(false);
    };

    const addTier = () => {
        if (editTiers.length > 0) {
            const last = editTiers[editTiers.length - 1];
            setEditTiers([...editTiers, { min: (last.max ?? last.min ?? 0) + 1, price: last.price }]);
            return;
        }
        setEditTiers([{ min: 2, price: editPrice }]);
    };

    const updateTier = (index, field, value) => {
        const next = [...editTiers];
        next[index] = { ...next[index], [field]: value ?? 0 };
        setEditTiers(next);
    };

    const deleteTier = (index) => {
        const next = [...editTiers];
        next.splice(index, 1);
        setEditTiers(next);
    };

    const currentPrice = priceItems[row.id]?.original_price ?? row.original_price ?? 0;
    const currentTiers = priceItems[row.id]?.price_list ?? row.price_list ?? [];

    return (
        <>
            <div>
                <Space>
                    <Text type="success" style={{ fontSize: 12 }}>{formatCurrency(currentPrice)}</Text>
                    <Button type="link" size="small" icon={<EditOutlined />} onClick={openModal} style={{ padding: 0 }} />
                </Space>
                {currentTiers.map((tier, i) => (
                    <div key={i} style={{ color: '#8c8c8c', fontSize: 12, marginTop: 2 }}>
                        Từ {tier.min}: {formatCurrency(tier.price)}
                    </div>
                ))}
            </div>

            <Modal
                title={`Sửa giá bán — ${row?.product?.name ?? ''} ${row.variant_title ?? ''}`}
                open={open}
                onCancel={() => setOpen(false)}
                footer={null}
            >
                <Space direction="vertical" size={8} style={{ width: '100%' }}>
                    <Text>Giá bán mặc định</Text>
                    <InputNumber value={editPrice} onChange={(v) => setEditPrice(v ?? 0)} style={{ width: '100%' }} />

                    <Button type="dashed" onClick={addTier} style={{ width: '100%' }}>+ Thêm khoảng giá</Button>

                    {editTiers.map((tier, i) => (
                        <Space key={i} style={{ width: '100%' }} align="start">
                            <InputNumber
                                value={tier.min}
                                onChange={(v) => updateTier(i, 'min', v)}
                                showCalculator={false}
                                style={{ width: 100 }}
                            />
                            <InputNumber
                                value={tier.price}
                                onChange={(v) => updateTier(i, 'price', v)}
                                style={{ flex: 1 }}
                            />
                            <Button
                                type="text"
                                danger
                                icon={<DeleteOutlined />}
                                onClick={() => deleteTier(i)}
                            />
                        </Space>
                    ))}

                    <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                        {siblingVariants && siblingVariants.length > 0 && (
                            <Button onClick={handleApplyToProduct}>Áp dụng biến thể khác</Button>
                        )}
                        <Button onClick={() => setOpen(false)}>Huỷ</Button>
                        <Button type="primary" onClick={handleApply}>Áp dụng</Button>
                    </Space>
                </Space>
            </Modal>
        </>
    );
};

const Mobile = ({ viewModel }) => {
    const {
        isEdit, name, setName,
        mode, setMode,
        applyTo, setApplyTo,
        tagIds, setTagIds,
        categoryIds, setCategoryIds,
        productIds, setProductIds,
        autoRule, setAutoRule,
        scopedVariants, priceItems, onPriceItemChange,
        categories,
        applyAutoRule,
        onSave, navigate,
    } = viewModel;

    const [searchText, setSearchText] = useState('');

    const displayedVariants = useMemo(() => {
        if (!searchText.trim()) return scopedVariants;
        const q = toSearchText(searchText);
        return scopedVariants.filter(v => toSearchText(v?.product?.name ?? '').includes(q));
    }, [scopedVariants, searchText]);

    const renderGiaGoc = (row) => {
        const tiers = row.price_list ?? [];
        return (
            <>
                <Text style={{ fontSize: 12 }}>{formatCurrency(row.original_price ?? 0)}</Text>
                {tiers.map((tier, i) => (
                    <div key={i} style={{ color: '#8c8c8c', fontSize: 12, marginTop: 2 }}>
                        Từ {tier.min}: {formatCurrency(tier.price)}
                    </div>
                ))}
            </>
        );
    };

    return (
        <div style={{ width: '100%', paddingBottom: 80 }}>
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
            <Space>
                <Button size="small" icon={<ArrowLeftOutlined />} onClick={() => navigate('/pricing')}>Quay lại</Button>
            </Space>

            <Card size="small" title="Thông tin chung">
                <Space direction="vertical" size={8} style={{ width: '100%' }}>
                    <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Tên bảng giá" />
                    <Radio.Group
                        options={ModeOptions}
                        value={mode}
                        onChange={(e) => setMode(e.target.value)}
                        optionType="button"
                        size="small"
                    />
                    {mode === 'auto' && (
                        <Space>
                            <Select
                                value={autoRule.type}
                                onChange={(v) => setAutoRule(prev => ({ ...prev, type: v }))}
                                options={AutoRuleTypeOptions}
                                size="small"
                                style={{ width: 120 }}
                            />
                            <AntInputNumber
                                value={autoRule.value}
                                min={0}
                                size="small"
                                onChange={(v) => setAutoRule(prev => ({ ...prev, value: v ?? 0 }))}
                                style={{ width: 100 }}
                            />
                        </Space>
                    )}
                </Space>
            </Card>

            <Card size="small" title="Phạm vi">
                <Space direction="vertical" size={8} style={{ width: '100%' }}>
                    <Select
                        value={applyTo}
                        onChange={setApplyTo}
                        options={ApplyToOptions}
                        size="small"
                        style={{ width: '100%' }}
                    />
                    {applyTo === 'tag' && (
                        <TagPicker
                            mode="multiple"
                            value={tagIds}
                            onChange={setTagIds}
                            size="small"
                            style={{ width: '100%' }}
                            placeholder="Chọn tag"
                        />
                    )}
                    {applyTo === 'category' && (
                        <Select
                            mode="multiple"
                            value={categoryIds}
                            onChange={setCategoryIds}
                            options={categories}
                            size="small"
                            style={{ width: '100%' }}
                            placeholder="Chọn danh mục"
                        />
                    )}
                    {applyTo === 'manual' && (
                        <ProductPicker
                            mode="multiple"
                            value={productIds}
                            onChange={setProductIds}
                            size="small"
                            style={{ width: '100%' }}
                            placeholder="Chọn sản phẩm"
                        />
                    )}
                </Space>
            </Card>

            <Card size="small" title={`Danh sách sản phẩm (${displayedVariants.length}/${scopedVariants.length} biến thể)`}>
                <Input
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    allowClear
                    size="small"
                    placeholder="Tìm tên sản phẩm..."
                    style={{ marginBottom: 8 }}
                />
                <List
                    size="small"
                    dataSource={displayedVariants}
                    rowKey="id"
                    pagination={displayedVariants.length > 20 ? { pageSize: 20, size: 'small' } : false}
                    renderItem={(row) => (
                        <List.Item>
                            <Space direction="vertical" size={2} style={{ width: '100%' }}>
                                <Text style={{ fontSize: 12 }}>{row?.product?.name} - {row.variant_title}</Text>
                                <div>{renderGiaGoc(row)}</div>
                                {mode === 'auto' ? (
                                    <div>
                                        <Text type="success" style={{ fontSize: 12 }}>
                                            {formatCurrency(applyAutoRule(row.original_price ?? 0, autoRule))}
                                        </Text>
                                        {(row.price_list ?? []).map((tier, i) => (
                                            <div key={i} style={{ color: '#8c8c8c', fontSize: 12, marginTop: 2 }}>
                                                Từ {tier.min}: {formatCurrency(applyAutoRule(tier.price ?? 0, autoRule))}
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <MobilePriceEditModal
                                        row={row}
                                        priceItems={priceItems}
                                        onPriceItemChange={onPriceItemChange}
                                        siblingVariants={scopedVariants.filter(v => v.product_id === row.product_id && v.id !== row.id)}
                                    />
                                )}
                            </Space>
                        </List.Item>
                    )}
                />
            </Card>

            <div
                style={{
                    position: 'fixed',
                    left: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: 1000,
                    background: '#fff',
                    borderTop: '1px solid #f0f0f0',
                    padding: '8px 12px calc(8px + env(safe-area-inset-bottom)) 12px',
                }}
            >
                <Button type="primary" block onClick={onSave}>Lưu bảng giá</Button>
            </div>
        </Space>
        </div>
    );
};

export default Mobile;
